import { useRef, useState } from 'react';
import { X, Upload, Loader2, AlertCircle, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { avatarModelsService } from '@/services/avatar-models.service';

interface Props {
  onClose: () => void;
  /** Called with the newly created avatar id on success, after the modal closes. */
  onCreated?: (avatar_id: string) => void;
  /** When set, the upload is added as another LOOK in this existing group instead of a new avatar */
  attach_to_group_id?: string;
  /** Display name of the existing group — used in the modal title when adding a look */
  attach_to_group_name?: string;
}

const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

export default function PrivateAvatarUploadModal({ onClose, onCreated, attach_to_group_id, attach_to_group_name }: Props) {
  const is_add_look_mode = !!attach_to_group_id;
  const [file, setFile] = useState<File | null>(null);
  const [preview_url, setPreviewUrl] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [gender, setGender] = useState<'' | 'male' | 'female' | 'other'>('');
  const [consent, setConsent] = useState(false);
  const [is_submitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const file_input_ref = useRef<HTMLInputElement>(null);

  const handleFileSelect = (incoming: File | null) => {
    setError(null);
    if (!incoming) {
      setFile(null);
      setPreviewUrl(null);
      return;
    }
    if (!ACCEPTED_TYPES.includes(incoming.type)) {
      setError('Use a JPG, PNG, or WebP image.');
      return;
    }
    if (incoming.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`Image is too large (max ${MAX_FILE_SIZE_MB} MB).`);
      return;
    }
    setFile(incoming);
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(incoming);
  };

  const can_submit = !!file && name.trim().length >= 2 && consent && !is_submitting;

  const handleSubmit = async () => {
    if (!can_submit || !file) return;
    setIsSubmitting(true);
    setError(null);
    toast.loading(
      is_add_look_mode ? 'Adding look to avatar group...' : 'Uploading and registering with HeyGen...',
      { id: 'upload-avatar' },
    );

    const result = await avatarModelsService.uploadPrivateAvatar({
      file,
      name: name.trim(),
      gender: gender || undefined,
      consent_acknowledged: consent,
      avatar_group_id: attach_to_group_id,
    });

    setIsSubmitting(false);

    if (!result.success) {
      const msg = result.error || 'Upload failed';
      setError(msg);
      toast.error(msg, { id: 'upload-avatar' });
      return;
    }

    toast.success(is_add_look_mode ? 'New look added!' : 'Avatar created!', { id: 'upload-avatar' });
    onCreated?.(result.avatar?.id || '');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-2 min-w-0">
            <Upload className="w-5 h-5 text-brand-purple-600 flex-shrink-0" />
            <h2 className="text-sm font-semibold text-gray-900 truncate">
              {is_add_look_mode
                ? `Add Look to "${attach_to_group_name || 'Avatar'}"`
                : 'Upload Photo Avatar'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* File picker / preview */}
          {!preview_url ? (
            <div
              onClick={() => file_input_ref.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => {
                e.preventDefault();
                const dropped = e.dataTransfer.files?.[0];
                if (dropped) handleFileSelect(dropped);
              }}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-brand-purple-400 hover:bg-brand-purple-50/30 transition-colors"
            >
              <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                <span className="text-brand-purple-600 font-medium">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG, or WebP · up to {MAX_FILE_SIZE_MB} MB · single front-facing face</p>
            </div>
          ) : (
            <div className="relative">
              <img
                src={preview_url}
                alt="Preview"
                className="w-full h-56 object-cover rounded-lg border border-gray-200"
              />
              <button
                type="button"
                onClick={() => handleFileSelect(null)}
                className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-black/80 text-white rounded-full"
                title="Remove image"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <input
            ref={file_input_ref}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
          />

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Display name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sarah from Marketing"
              maxLength={80}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:ring-1 focus:ring-brand-purple-300 focus:border-brand-purple-300"
              disabled={is_submitting}
            />
          </div>

          {/* Gender */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Gender (optional)</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value as '' | 'male' | 'female' | 'other')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white"
              disabled={is_submitting}
            >
              <option value="">Prefer not to say</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Engine notice */}
          <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-[11px] text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Uses Avatar IV.</strong> Uploaded avatars run on HeyGen's Avatar IV engine at $3/min (vs $1/min for stock avatars on Avatar III). A one-time $1 fee is charged for the avatar creation.
            </div>
          </div>

          {/* Consent */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-brand-purple-600 focus:ring-brand-purple-500"
              disabled={is_submitting}
            />
            <span className="text-xs text-gray-600 leading-relaxed">
              I confirm I have the rights to use this person's likeness for AI video generation. HeyGen will process this image.
            </span>
          </label>

          {/* Error */}
          {error && (
            <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={is_submitting}
            className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!can_submit}
            className="px-4 py-1.5 bg-brand-purple-600 text-white rounded-lg text-xs font-medium hover:bg-brand-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
          >
            {is_submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {is_submitting ? 'Uploading…' : is_add_look_mode ? 'Add Look' : 'Upload Avatar'}
          </button>
        </div>
      </div>
    </div>
  );
}
